import { Line, LineChart, ReferenceLine } from 'recharts';
import useCalendarChart, { CalendarChartProps } from './CalendarChart';
import { colors } from 'design';

/**
 * The good-to-bad ramp painted behind the line.
 *
 * The offsets are uneven on purpose — they place each colour where its band of
 * values actually falls on the axis, rather than spreading them evenly. The
 * colours come from the shared `dataViz` scale so this chart cannot drift away
 * from the legends and the other trackers that use the same five steps.
 */
const GRADIENT_STOPS = [
  { offset: '0%', color: colors.dataViz.veryGood },
  { offset: '23%', color: colors.dataViz.good },
  { offset: '58%', color: colors.dataViz.average },
  { offset: '75%', color: colors.dataViz.bad },
  { offset: '100%', color: colors.dataViz.veryBad },
];

export type LinearCalendarChartProps = CalendarChartProps & {
  dayValues: (number | null)[];
  reverseColors?: boolean;
  range: number[];
  horizontalGridValues?: number[];
  horizontalGridValuesLight?: number[];
};

const LinearCalendarChart: React.FC<LinearCalendarChartProps> = ({
  dayLabels,
  dayValues,
  reverseColors = false,
  range,
  horizontalGridValues,
  horizontalGridValuesLight,
  dayRange,
  periodDots,
  drawHollowPeriodDots = false,
  topLabels,
  showMonthLabels = false,
  yRangeOverride,
}) => {
  // Hooks
  const { chartBuildingBlocks, graphHeight, yRange, simpleGraphHeight, topLabelsHeight, domainMaxDay, domainMinDay } =
    useCalendarChart({
      dayLabels,
      dayRange,
      periodDots,
      drawHollowPeriodDots,
      topLabels,
      showMonthLabels,
      yRangeOverride,
    });
  const [unsafeRangeMin, unsafeRangeMax] = range;
  const rangeMax = unsafeRangeMax + 0.002;
  const rangeMin = unsafeRangeMin - 0.002;

  const offset = dayLabels[0].diff(domainMinDay, 'days');

  // data maps to [1.5, yRange - 0.5], leaving 1.5 units below (day bar) and 0.5 units above as margin
  const dataSpan = yRange - 2;
  const normalizedValues = dayValues.map((v, i) => {
    if (v === null) return null;
    const normalized = (v - rangeMin) / (rangeMax - rangeMin);
    return {
      dayValue: normalized * dataSpan + 1.5,
      dayIndex: i + 0.5 + offset, // adding .5 to the day index to plot points in the center of day columns
    };
  });

  // for the gradient percentages, we scale the percentage by the proportion of actual graph compared to the total drawing,
  // and take into account the offset from extra elements at the top of the graph
  const gradientStart = (0.8 / yRange) * simpleGraphHeight + topLabelsHeight;
  const gradientEnd = ((yRange - 1.8) / yRange) * simpleGraphHeight + topLabelsHeight;
  // green (top) to red (bottom) gradient
  const gradientName = `higherIsBetter-${rangeMin}-${rangeMax}-${topLabelsHeight}`;
  // green (bottom) to red (top) gradient
  const reverseGradientName = `lowerIsBetter-${rangeMin}-${rangeMax}-${topLabelsHeight}`;

  const normalizeY = (v: number) => ((v - rangeMin) / (rangeMax - rangeMin)) * dataSpan + 1.5;
  const toSvgY = (dataY: number) => ((yRange - dataY) / yRange) * simpleGraphHeight;
  const chartWidth = (domainMaxDay.diff(domainMinDay, 'days') + 1) * 14;

  return (
    <LineChart
      width={chartWidth}
      height={graphHeight}
      data={normalizedValues.some(v => v !== null) ? normalizedValues : [{ dayValue: -1, dayIndex: -1 }]}
      margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
    >
      {chartBuildingBlocks}
      <defs>
        <linearGradient
          id={gradientName}
          x1="0%"
          x2="0%"
          y1={gradientStart}
          y2={gradientEnd}
          gradientUnits="userSpaceOnUse"
        >
          {GRADIENT_STOPS.map(({ offset, color }) => (
            <stop key={offset} offset={offset} stopColor={color} />
          ))}
        </linearGradient>
        <linearGradient
          id={reverseGradientName}
          x1="0%"
          x2="0%"
          y1={gradientEnd}
          y2={gradientStart}
          gradientUnits="userSpaceOnUse"
        >
          {GRADIENT_STOPS.map(({ offset, color }) => (
            <stop key={offset} offset={offset} stopColor={color} />
          ))}
        </linearGradient>
      </defs>
      {horizontalGridValuesLight?.map(v => (
        <ReferenceLine key={`light-${v}`} y={normalizeY(v)} stroke={colors.border.subtle} strokeWidth={1} />
      ))}
      {horizontalGridValues?.map(v => (
        <ReferenceLine key={v} y={normalizeY(v)} stroke={colors.dataViz.grid} strokeWidth={1} />
      ))}
      {horizontalGridValues?.map(v => (
        <text
          key={`label-${v}`}
          x={chartWidth - 2}
          y={toSvgY(normalizeY(v)) - 2}
          textAnchor="end"
          fill={colors.dataViz.axis}
          style={{ fontSize: 10 }}
        >
          {v}°
        </text>
      ))}
      <Line
        type="monotone"
        dataKey="dayValue"
        animationDuration={100}
        stroke={reverseColors ? `url(#${reverseGradientName})` : `url(#${gradientName})`}
        strokeWidth={4}
        dot={{ strokeWidth: 2 }}
      />
    </LineChart>
  );
};
export default LinearCalendarChart;
